import React from 'react'

import { ErrorAlert } from 'pivotal-ui/react/alerts'
import {
  AffectedProduct, AffectedRelease, DependentsNotSatisfiedProduct, ErrorInfoState, InconsistentStateErrorInfoState,
  PlanErrorTypes, VersionNotFoundErrorInfoState
} from '../error_info/error_info_types'
import {TAS_NAME} from "../constants/constants";

const DefaultError = () => (
  <div>
    We couldn't find an upgrade plan for you. Please double check the data you provided and
    contact <a href="https://www.broadcom.com/support" target='_blank' rel="noopener noreferrer">Broadcom
    Support</a> for further assistance.
  </div>
)

const TimeoutError = () => (
  <div>
    Upgrade plan creation timed out. Please try again.
  </div>
)

const InternalServerError = () => (
  <div>
    Something went wrong on our end while generating your plan. Please try again in a few minutes.
    If this keeps happening, contact <a href="https://www.broadcom.com/support" target='_blank' rel="noopener noreferrer">Broadcom
    Support</a>.
  </div>
)

const InconsistentStateError = ({error}: { error: InconsistentStateErrorInfoState }) => (
  error.type === PlanErrorTypes.INCONSISTENT_STATE_CURRENT ? (
    <div>
      Some of the product versions installed in your foundation have dependencies that aren't met:

      {formatReleases(error.affected)}

      Please review these requirements on the Broadcom Support Portal and adjust your plan to ensure all prerequisites are satisfied.
    </div>
  ) : (
    <div>
      Some of your selected target versions have dependencies that aren't met:

      {formatReleases(error.affected)}

      Please check the required versions on the Broadcom Support Portal and update your target selections to resolve these dependencies.
    </div>
  )
)

const DowngradeError = ({products}: { products: DowngradedProduct[] }) => (

  <div>
    Product downgrades are not supported. Please fix the following products:
    {formatProducts(products)}
  </div>
)

const PathNotFoundError = ({products}: { products: PathNotFoundProduct[] }) => {
  let product = products[0]
  let currentVersion = product.currentVersion
  let targetVersion = product.targetVersion
  let productLink = null
  if (product.link !== undefined) {
    productLink =
      <a href={product.link} target='_blank' rel="noopener noreferrer" className='release-link'>{product.name}</a>
  }
  return (
    <div>
      We couldn't find an upgrade plan for you. The release metadata on Broadcom's Support Portal
      for {productLink === null ? product.name : productLink} suggests that
      there is no upgrade path from {currentVersion} to {targetVersion}. Please try again and if this error still
      appears, contact
      <a href="https://www.broadcom.com/support" target='_blank' rel="noopener noreferrer">Broadcom Support</a> for
      further assistance.
    </div>
  )
}

const DepNotSatisfiedError = ({product}: { product: DependentsNotSatisfiedProduct }) => {
  return (
    <div>
      We couldn't find an upgrade plan for you. The release metadata on Broadcom's Support Portal for {product.name} suggests
      that there is no upgrade path from {product.currentVersion} to {product.targetVersion} that
      maintains compatibility with other tiles. Please try again and if this error still appears,
      contact <a href="https://www.broadcom.com/support" target='_blank' rel="noopener noreferrer">Broadcom Support</a> for
      further assistance.
    </div>
  )
}

const NoValidStatesError = () => (
  <div>
    We couldn't find an upgrade plan for you. There was an issue generating upgrade phases that maintain compatibility
    for the product releases selected.
    Please check the data you entered, try again and if this error still appears
    contact <a href="https://www.broadcom.com/support" target='_blank' rel="noopener noreferrer">Broadcom Support</a> for
    further assistance.
  </div>
)

const VersionNotFoundProductLabel = ({product}: { product: VersionNotFoundProduct }) => {
  const name = product.link
    ? <a href={product.link} target='_blank' rel="noopener noreferrer" className='release-link'>{product.name}</a>
    : <>{product.name}</>
  return <>{name} {product.targetVersion}</>
}

const VersionNotFoundError = ({error}: { error: VersionNotFoundErrorInfoState }) => (
  <div>
    {error.affected.length === 1 ? (
      <p><VersionNotFoundProductLabel product={error.affected[0]}/> is not available on Broadcom's Support Portal.</p>
    ) : (
      <>
        <p>The following product versions are not available on Broadcom's Support Portal:</p>
        <ul>
          {error.affected.map((product: VersionNotFoundProduct) => (
            <li key={product.slug}><VersionNotFoundProductLabel product={product}/></li>
          ))}
        </ul>
      </>
    )}
    <p>
      This can happen when a product version has not yet been published. Please verify the version is
      released on Broadcom's Support Portal before trying again.
    </p>
  </div>
)

const TasRoutingBugError = ({versions}: { versions: string[] }) => (
  <div>
    <p>The target version of {TAS_NAME} you have selected contains a known bug in its routing-release component, so we have disabled
        upgrades to this version to protect against app downtime.  Please select a newer version of {TAS_NAME} to ensure the
        stability of your foundation.</p>

    <p><b>Affected {TAS_NAME} versions:</b> {versions.join(', ')}</p>

    <p>For further assistance, please
      review <a href="https://knowledge.broadcom.com/external/article?articleNumber=374487" target='_blank' rel="noopener noreferrer">
      the documentation for this bug</a> or contact <a href="https://www.broadcom.com/support" target='_blank' rel="noopener noreferrer">
      Broadcom Support</a>.</p>
  </div>
)

const DuplicateSlugConflictError = ({slug}: { slug: string }) => (
  <div>
    Your input lists <b>{slug}</b> more than once with different versions, so we couldn't tell
    which entry to plan with. Please remove the duplicate entry (or align the versions) and try again.
  </div>
)

type ErrorProduct = {
  name: string
  slug: string
  link: string
}

export type DowngradedProduct = ErrorProduct

export type PathNotFoundProduct = ErrorProduct & {
  currentVersion: string
  targetVersion: string
}

export type VersionNotFoundProduct = ErrorProduct & {
  currentVersion: string
  targetVersion: string
}

const PlanStepsError = ({error}: { error: ErrorInfoState }) => {
  let errorContent
  switch (error.type) {

  case PlanErrorTypes.INCONSISTENT_STATE_CURRENT:
  case PlanErrorTypes.INCONSISTENT_STATE_TARGET:
    errorContent = <InconsistentStateError error={error}/>
    break
  case PlanErrorTypes.DEPENDENTS_NOT_SATISFIED:
    errorContent = <DepNotSatisfiedError product={error.affected}/>
    break
  case PlanErrorTypes.DOWNGRADE:
    errorContent = <DowngradeError products={error.affected}/>
    break
  case PlanErrorTypes.PATH_NOT_FOUND:
    errorContent = <PathNotFoundError products={error.affected}/>
    break
  case PlanErrorTypes.TIMEOUT:
    errorContent = <TimeoutError/>
    break
  case PlanErrorTypes.NO_VALID_STATES:
    errorContent = <NoValidStatesError/>
    break
  case PlanErrorTypes.TAS_ROUTING_BUG:
    errorContent = <TasRoutingBugError versions={error.affected}/>
    break
  case PlanErrorTypes.VERSION_NOT_FOUND:
    errorContent = <VersionNotFoundError error={error}/>
    break
  case PlanErrorTypes.INTERNAL_SERVER_ERROR:
    errorContent = <InternalServerError/>
    break
  case PlanErrorTypes.DUPLICATE_SLUG_CONFLICT:
    errorContent = <DuplicateSlugConflictError slug={error.slug}/>
    break
  default:
    errorContent = <DefaultError/>
  }

  return (
    <ErrorAlert withIcon>
      {errorContent}
    </ErrorAlert>
  )
}
const formatReleases = (releases: AffectedRelease[]) => {
  return releases.map((release: AffectedRelease) => {
    let itemAfterText = null
    let dependencyLinks = null

    if (release.dependencies !== undefined) {
      dependencyLinks = <ul>{
        release.dependencies.map((dep: AffectedProduct) => {
          let versionLinks = dep.versionSpecifiers
          let versionLinkText = versionLinks.reduce((acc, curr, index, arr) => {
            acc += ', '

            if (index === arr.length - 1) {
              acc += 'or '
            }

            acc += curr

            return acc
          })

          return <li key={`${release.slug}-${dep.slug}`}>
            <a
              href={dep.link} target='_blank' rel="noopener noreferrer"
              className='release-link'>{dep.name}</a> {versionLinkText}
          </li>
        })}
      </ul>
      itemAfterText = ' requires'
    }

    let item
    if (release.link) {
      item = <a href={release.link} target="_blank" rel="noopener noreferrer" className='release-link'>
        {release.name}
      </a>
    } else {
      item = release.name
    }

    return (
      <ul key={'error-info-' + release.slug}>
        <li>
          {item}{itemAfterText}
        </li>
        {dependencyLinks}
      </ul>
    )
  })
}

const formatProducts = (products: DowngradedProduct[]) => {
  return (
    <ul>{
      products.map((product: DowngradedProduct) => {
        return (
          <li key={`${product.slug}-${product.slug}`}>
            <a href={product.link} target='_blank' rel="noopener noreferrer" className='release-link'>{product.name}</a>
          </li>
        )
      })}
    </ul>
  )
}

export default PlanStepsError
